/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button as ButtonAnt,
  Col,
  Form,
  FormProps,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { Icon } from '../icons';
import { Button } from '../button';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getListRoles, watchRoleList } from '@/redux/slices/roles.slice';
import {
  getAdminUserById,
  resetMessages,
  updateAdminUser,
  watchAdminUserDetail,
  watchAdminUserLoading,
  watchAdminUserMessage
} from '@/redux/slices/admin.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { getProfileUser } from '@/redux/slices/auth.slice';
import { useBeforeUnload } from 'react-router-dom';

type FieldType = {
  name: string;
  email: string;
  roleIds: number[];
};

export const AdminEditForm = () => {
  const [form] = Form.useForm();
  const { push, adminId } = useQuery();
  const dispatch = useAppDispatch();
  const messageInfo = useAppSelector(watchAdminUserMessage);
  const rolesList = useAppSelector(watchRoleList);
  const loading = useAppSelector(watchAdminUserLoading);
  const userAdminDetail = useAppSelector(watchAdminUserDetail);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const rolesOptions = useMemo(
    () =>
      rolesList.map((role) => ({
        value: role.id,
        label: role.name
      })),
    [rolesList]
  );

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofAdminManagement());
  }, [push]);

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const result = await dispatch(
      updateAdminUser({ id: adminId, roleIds: values.roleIds })
    );

    if (updateAdminUser.fulfilled.match(result)) {
      await dispatch(getProfileUser());
      handleGoBack();
    }
  };

  useEffect(() => {
    dispatch(getListRoles());
  }, [dispatch]);

  useEffect(() => {
    if (!adminId) {
      handleGoBack();
    } else {
      dispatch(getAdminUserById(adminId));
    }
  }, [adminId, dispatch, handleGoBack]);

  useEffect(() => {
    if (userAdminDetail) {
      form.setFieldsValue({
        email: userAdminDetail.email,
        roleIds: userAdminDetail.role.map((role) => role.id),
        name: userAdminDetail.fullName
      });
    }
  }, [userAdminDetail, form]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo, form]);

  useBeforeUnload((e) => {
    e.preventDefault();
  });

  return (
    <div css={rootStyles}>
      <Typography.Title css={titleStyles} level={5}>
        <ButtonAnt
          css={goBackBtnStyles}
          onClick={handleGoBack}
          type='text'
          icon={<Icon type='arrowLeft' width={20} height={20} />}
          iconPosition='start'
        />
        Cập nhật quản trị viên
      </Typography.Title>
      <Form
        form={form}
        css={formStyles}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        name='create-admin'
        autoComplete='off'
        className='ant-form-custom'
      >
        <Form.Item<FieldType>
          label='Email (*)'
          name='email'
          rules={[{ required: true, message: 'Vui lòng nhập email!' }]}
        >
          <Input
            css={inputStyles}
            size='large'
            placeholder='Nhập email'
            disabled
          />
        </Form.Item>

        <Form.Item<FieldType>
          label='Vai trò (*)'
          name='roleIds'
          rules={[{ required: true, message: 'Vui lòng chọn vai trò!' }]}
        >
          <Select
            css={inputStyles}
            showSearch
            onChange={setSelectedItems}
            value={selectedItems}
            options={rolesOptions}
            mode='multiple'
            placeholder='Chọn vai trò'
            size='large'
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item<FieldType> label='Tên quản trị viên' name='name'>
          <Input css={inputStyles} size='large' disabled />
        </Form.Item>

        <Row css={formBottomStyles} justify='center' gutter={[20, 0]}>
          <Col>
            <Button onClick={handleGoBack} css={actionBtnStyles} shape='round'>
              Huỷ bỏ
            </Button>
          </Col>
          <Col>
            <Button
              loading={loading}
              css={actionBtnStyles}
              type='primary'
              shape='round'
              htmlType='submit'
            >
              Lưu
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
`;

const formStyles = css`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  .ant-form-item {
    .ant-form-item-label {
      width: 14rem;
      text-align: left;
      font-weight: 500;
      line-height: 5rem;
    }
  }
`;

const inputStyles = css`
  height: 5rem;
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;

const formBottomStyles = css`
  margin-top: 2rem;
`;
